import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { MenuToggleButton } from './HeaderBar';
import styled from 'styled-components';
import { Dropdown, MenuProps } from 'antd';
import ReactAvatar from 'react-avatar';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
// react-icons types are not yet compatible with React 19 typings – cast to any for now
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaBible as FaBibleRaw,
  FaBell as FaBellRaw,
  FaBullhorn as FaBullhornRaw,
  FaChalkboardTeacher as FaChalkboardTeacherRaw,
  FaCalendarAlt as FaCalendarAltRaw,
  FaChartLine as FaChartLineRaw,
  FaChevronDown as FaChevronDownRaw,
  FaChild as FaChildRaw,
  FaFileSignature as FaFileSignatureRaw,
  FaHandHoldingHeart as FaHandHoldingHeartRaw,
  FaHandshake as FaHandshakeRaw,
  FaHandsHelping as FaHandsHelpingRaw,
  FaHeart as FaHeartRaw,
  FaHome as FaHomeRaw,
  FaInbox as FaInboxRaw,
  FaListUl as FaListUlRaw,
  FaMicrophoneAlt as FaMicrophoneAltRaw,
  FaPaperPlane as FaPaperPlaneRaw,
  FaPrayingHands as FaPrayingHandsRaw,
  FaRegCommentDots as FaRegCommentDotsRaw,
  FaReceipt as FaReceiptRaw,
  FaSearch as FaSearchRaw,
  FaClock as FaClockRaw,
  FaStore as FaStoreRaw,
  FaTags as FaTagsRaw,
  FaUserCheck as FaUserCheckRaw,
  FaUserCog as FaUserCogRaw,
  FaBug as FaBugRaw,
  FaUserShield as FaUserShieldRaw,
  FaUser as FaUserRaw,
  FaUsers as FaUsersRaw,
} from 'react-icons/fa';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { FiMenu as FiMenuRaw } from 'react-icons/fi';

import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { useMyEventAdminEventIds } from '../hooks/useEventAdmins';
import { useKidsChurchChatUsers } from '../hooks/useKidsChurchChatUsers';
import { useAdminChatUsers } from '../hooks/useAdminChatUsers';
import { usePendingApprovalsCount } from '../hooks/usePendingApprovalsCount';
import { useWebsiteContactRequests } from '../hooks/useWebsiteContactRequests';
import { useUnreadNotificationCount } from '../hooks/useNotifications';
import { useOpenAppIssueTicketCount } from '../hooks/useAppIssueTickets';
import { usePendingBusinessesCount } from '../hooks/usePendingBusinesses';
import { Permission, hasAnyPermission, hasAnyRole, hasRole } from '../utils/roles';
import KimiChatWidget from './chat/KimiChatWidget';

// Cast to any to satisfy TS2786 until upstream types support React 19
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBible: any = FaBibleRaw;
const FaBell: any = FaBellRaw;
const FaBullhorn: any = FaBullhornRaw;
const FaChalkboardTeacher: any = FaChalkboardTeacherRaw;
const FaCalendarAlt: any = FaCalendarAltRaw;
const FaChartLine: any = FaChartLineRaw;
const FaClock: any = FaClockRaw;
const FaChevronDown: any = FaChevronDownRaw;
const FaChild: any = FaChildRaw;
const FaFileSignature: any = FaFileSignatureRaw;
const FaHandHoldingHeart: any = FaHandHoldingHeartRaw;
const FaHandshake: any = FaHandshakeRaw;
const FaHandsHelping: any = FaHandsHelpingRaw;
const FaHeart: any = FaHeartRaw;
const FaHome: any = FaHomeRaw;
const FaInbox: any = FaInboxRaw;
const FaListUl: any = FaListUlRaw;
const FaMicrophoneAlt: any = FaMicrophoneAltRaw;
const FaPaperPlane: any = FaPaperPlaneRaw;
const FaPrayingHands: any = FaPrayingHandsRaw;
const FaRegCommentDots: any = FaRegCommentDotsRaw;
const FaReceipt: any = FaReceiptRaw;
const FaSearch: any = FaSearchRaw;
const FaStore: any = FaStoreRaw;
const FaTags: any = FaTagsRaw;
const FaUserCheck: any = FaUserCheckRaw;
const FaUserCog: any = FaUserCogRaw;
const FaBug: any = FaBugRaw;
const FaUserShield: any = FaUserShieldRaw;
const FaUser: any = FaUserRaw;
const FaUsers: any = FaUsersRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FiMenu: any = FiMenuRaw;

const MOBILE_BREAKPOINT = '960px';
const DESKTOP_SIDEBAR_WIDTH = '258px';
const DESKTOP_LAYOUT_GAP = '1.75rem';

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--bg);
  position: relative;
  gap: 0;
`;

const Sidebar = styled.aside<{ $open: boolean }>`
  width: ${DESKTOP_SIDEBAR_WIDTH};
  background: linear-gradient(180deg, var(--bg) 0%, var(--surfaceVariant) 100%);
  color: var(--text);
  padding: 1.2rem 1rem 0.9rem 1.15rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  flex-shrink: 0;
  overflow: visible;
  border-right: 1px solid var(--outlineVariant);

  @media (min-width: 961px) {
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    z-index: 90;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    position: fixed;
    inset: 0 auto 0 0;
    height: 100vh;
    transform: translateX(${({ $open }) => ($open ? '0' : '-105%')});
    box-shadow: ${({ $open }) =>
      $open ? '14px 0 36px rgba(0, 0, 0, 0.34)' : 'none'};
    z-index: 160;
    overflow: hidden;
    -webkit-overflow-scrolling: touch;
    padding-left: max(1.1rem, env(safe-area-inset-left));
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
  }
`;

const SidebarHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  padding: 0.2rem 0.15rem 0.75rem;
  border-bottom: 1px solid var(--outlineVariant);
`;

const SidebarDashboardTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.01em;
`;

const SidebarNav = styled.nav`
  flex: 1;
  min-height: auto;
  overflow-y: visible;
  overflow-x: hidden;
  padding-right: 0.2rem;
  padding-top: 0.1rem;
  scrollbar-width: thin;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 0;
    overflow-y: auto;
  }
`;

const SidebarLink = styled(NavLink)`
  color: var(--navInactive);
  margin: 0.2rem 0;
  padding: 0.62rem 0.72rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border-radius: 12px;
  line-height: 1.25;
  font-size: 0.94rem;
  transition: background 0.16s ease, color 0.16s ease, transform 0.16s ease;
  svg {
    flex-shrink: 0;
  }
  &:hover {
    color: var(--text);
    background: var(--surfaceVariant);
    transform: translateX(2px);
  }
  &.active {
    color: var(--text);
    font-weight: 600;
    background: var(--surface);
    box-shadow: inset 3px 0 0 var(--primary);
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 44px;
    padding: 0.65rem 0.8rem;
  }
`;

// Sub link with indentation for nested pages
const SubSidebarLink = styled(SidebarLink)`
  margin-left: 0.75rem;
  padding-left: 0.9rem;
  font-size: 0.88rem;
  opacity: 0.95;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 44px;
  }
`;

const NavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  font-size: 0.98rem;
  opacity: 0.92;
`;

const NavCountBadge = styled.span`
  margin-left: auto;
  background: var(--error);
  color: var(--onError);
  border-radius: 9999px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  min-width: 18px;
  text-align: center;
`;

const SidebarSectionHeader = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  color: var(--navInactive);
  margin: 1.05rem 0 0.34rem 0;
  padding: 0.34rem 0.5rem;
  cursor: pointer;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 10px;
  transition: background 0.16s ease, color 0.16s ease;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--text);
  }
  svg {
    transition: transform 0.2s ease;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 44px;
    padding: 0.25rem 0.5rem;
  }
`;

const SidebarSectionTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

const SidebarSectionMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;

  ${NavCountBadge} {
    margin-left: 0;
    text-transform: none;
    letter-spacing: 0;
  }
`;

const SidebarSectionControls = ({
  isOpen,
  count = 0,
}: {
  isOpen: boolean;
  count?: number;
}) => (
  <SidebarSectionMeta>
    {count > 0 ? <NavCountBadge>{count}</NavCountBadge> : null}
    <FaChevronDown
      size={12}
      style={{
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    />
  </SidebarSectionMeta>
);

type SidebarSectionKey =
  | 'dashboard'
  | 'communications'
  | 'community'
  | 'events'
  | 'sermons'
  | 'serving'
  | 'nextSteps'
  | 'classes'
  | 'website'
  | 'administration';

const SidebarDivider = styled.div`
  height: 1px;
  background: var(--outline);
  margin: 0.95rem 0.35rem;
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding-top: 0.95rem;
  border-top: 1px solid var(--outline);
  position: relative;
  background: linear-gradient(180deg, rgba(18, 18, 18, 0.9) 0%, var(--bg) 40%);
  padding-bottom: max(1.35rem, env(safe-area-inset-bottom));
`;

const SidebarProfileTrigger = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.58rem 0.5rem;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  min-height: 44px;
  transition: background 0.16s ease;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const SidebarProfileMeta = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.125rem;
`;

const SidebarProfileName = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarProfileEmail = styled.span`
  font-size: 0.75rem;
  color: var(--navInactive);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarOpenButton = styled(MenuToggleButton)`
  position: fixed;
  top: max(1rem, env(safe-area-inset-top));
  left: max(1rem, env(safe-area-inset-left));
  z-index: 130;
`;

const Content = styled.main<{ $sidebarOpen: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 2rem;
  background: var(--bg);

  @media (min-width: 961px) {
    margin-left: ${({ $sidebarOpen }) =>
      $sidebarOpen ? `calc(${DESKTOP_SIDEBAR_WIDTH} + ${DESKTOP_LAYOUT_GAP})` : '0'};
    transition: margin-left 0.25s ease;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-left: 0;
    padding: 1rem;
  }
`;

const ContentShell = styled.div`
  min-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: calc(100vh - 2rem);
    gap: 0.85rem;
  }
`;

const ContentTopBar = styled.div`
  position: sticky;
  top: max(0.6rem, env(safe-area-inset-top));
  z-index: 70;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  pointer-events: auto;
`;

const TopBarIconButton = styled.button<{ $active?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 38%, transparent)'
        : 'color-mix(in srgb, var(--outline) 82%, transparent)'};
  background: ${({ $active }) =>
    $active
      ? 'color-mix(in srgb, var(--primary) 12%, var(--surface))'
      : 'color-mix(in srgb, var(--surface) 90%, transparent)'};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--text)')};
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12px);
  cursor: pointer;
  transition: transform 0.18s ease, background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    background: color-mix(in srgb, var(--surfaceVariant) 78%, var(--surface));
  }
`;

const TopBarIconBadge = styled.span`
  position: absolute;
  top: -3px;
  right: -1px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--error);
  color: var(--onError);
  border: 2px solid var(--bg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`;

const ContentBody = styled.div`
  min-height: 0;
`;

const SidebarOverlay = styled.div<{ $visible: boolean }>`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: ${({ $visible }) => ($visible ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 120;
  }
`;

export const Layout: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const { data: assignedEventIds = [] } = useMyEventAdminEventIds(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canViewDashboard = hasAnyPermission(roles, [Permission.VIEW_DASHBOARD]);
  const canViewMarketing = hasAnyPermission(roles, [Permission.VIEW_MARKETING]);
  const canViewAnnouncements = hasAnyPermission(roles, [
    Permission.MANAGE_CONTENT,
    Permission.VIEW_ANNOUNCEMENTS,
  ]);
  const canManageContent = hasAnyPermission(roles, [Permission.MANAGE_CONTENT]);
  const canViewSermonContent = hasAnyPermission(roles, [Permission.VIEW_SERMON_CONTENT]);
  const canViewSermonQuestions = hasAnyPermission(roles, [Permission.VIEW_SERMON_QUESTIONS]);
  const canManageEvents = hasAnyPermission(roles, [Permission.MANAGE_EVENTS]);
  const canManageAssignedEventRegistrants = assignedEventIds.length > 0;
  const canSubmitEventRequests = hasAnyPermission(roles, [Permission.SUBMIT_EVENT_REQUESTS]);
  const canViewChurchPlanner = hasAnyPermission(roles, [Permission.VIEW_CHURCH_PLANNER]);
  const canManageFacilities = hasAnyPermission(roles, [Permission.MANAGE_FACILITIES]);
  const canModerateCommunity = hasAnyPermission(roles, [Permission.MODERATE_COMMUNITY]);
  const canViewCommunityGroups = hasAnyPermission(roles, [Permission.VIEW_DASHBOARD]);
  const canManageVolunteers = hasAnyPermission(roles, [Permission.MANAGE_VOLUNTEERS]);
  const canViewPartnerRequests = hasAnyPermission(roles, [Permission.VIEW_PARTNER_REQUESTS]);
  const canManageKidsChurch = hasAnyPermission(roles, [Permission.MANAGE_KIDS_CHURCH]);
  const canViewUsers = hasAnyPermission(roles, [Permission.VIEW_USERS]);
  const canViewSuperAdmin = hasAnyPermission(roles, [Permission.VIEW_SUPER_ADMIN]);
  const canViewFinance = hasAnyPermission(roles, [Permission.VIEW_FINANCE]);
  const canViewGrowth = hasAnyPermission(roles, [Permission.VIEW_GROWTH]);
  const canViewGroupGoals = hasAnyPermission(roles, [Permission.VIEW_GROUP_GOALS]);
  const canManageSermonQuestions = hasAnyPermission(roles, [Permission.MANAGE_SERMON_QUESTIONS]);
  const isMinistryLeader = hasRole(roles, 'leader');
  const hasElevatedEventRole = hasAnyRole(roles, ['super_admin', 'admin', 'pastor']);
  const canViewAllEventLinks = canManageEvents && (!isMinistryLeader || hasElevatedEventRole);
  const isStaff =
    canViewDashboard ||
    canViewMarketing ||
    canViewAnnouncements ||
    canManageContent ||
    canViewSermonContent ||
    canViewSermonQuestions ||
    canManageEvents ||
    canManageAssignedEventRegistrants ||
    canSubmitEventRequests ||
    canModerateCommunity ||
    canManageVolunteers ||
    canManageKidsChurch ||
    canViewUsers ||
    canViewSuperAdmin ||
    canViewFinance ||
    canViewGrowth ||
    canViewGroupGoals ||
    canManageSermonQuestions ||
    canViewChurchPlanner ||
    canManageFacilities;
  const canViewCommunitySection =
    canViewCommunityGroups ||
    canModerateCommunity ||
    canViewGroupGoals;
  const canViewEventsFacilitiesSection =
    canViewAllEventLinks ||
    canManageEvents ||
    canManageAssignedEventRegistrants ||
    canSubmitEventRequests ||
    canManageFacilities ||
    canManageVolunteers;
  const canViewSermonsContentSection =
    canManageContent ||
    canViewSermonContent ||
    canManageSermonQuestions ||
    canViewSermonQuestions;
  const canViewServingVolunteersSection = canViewChurchPlanner || canManageVolunteers;
  const canViewNextStepsSection =
    canManageVolunteers || canViewPartnerRequests || canViewUsers || (canViewUsers && canViewGrowth);
  const canViewWelcomeInPartnerConversions =
    canViewUsers && (canManageVolunteers || canViewPartnerRequests);
  const canViewWebsiteSection = canViewUsers && canManageContent;
  const canViewAdministrationSection =
    canViewUsers || canViewFinance || canViewSuperAdmin || canManageContent;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openSections, setOpenSections] = useState<Record<SidebarSectionKey, boolean>>({
    dashboard: false,
    communications: false,
    community: false,
    events: false,
    sermons: false,
    serving: false,
    nextSteps: false,
    classes: false,
    website: false,
    administration: false,
  });

  const kidsChatUserId = canManageKidsChurch ? user?.id ?? '' : '';
  const { data: kidsChatUsers = [] } = useKidsChurchChatUsers(kidsChatUserId);
  const kidsChurchUnreadCount = useMemo(
    () => kidsChatUsers.reduce((sum, chatUser) => sum + (chatUser.unreadCount ?? 0), 0),
    [kidsChatUsers]
  );
  const adminChatUserId = canViewUsers ? user?.id ?? '' : '';
  const { data: adminChatUsers = [] } = useAdminChatUsers(adminChatUserId);
  const adminUnreadCount = useMemo(
    () => adminChatUsers.reduce((sum, chatUser) => sum + (chatUser.unreadCount ?? 0), 0),
    [adminChatUsers]
  );
  const { data: websiteContactRequests = [] } = useWebsiteContactRequests(canViewUsers);
  const contactRequestsCount = useMemo(
    () => websiteContactRequests.filter((request) => !request.is_read).length,
    [websiteContactRequests]
  );
  const { count: pendingApprovalsCount } = usePendingApprovalsCount(canManageContent);
  const { data: unreadNotificationsCount = 0 } = useUnreadNotificationCount(user?.id);
  const { data: openAppIssueCount = 0 } = useOpenAppIssueTicketCount(canViewSuperAdmin);
  const { data: pendingBusinessesCount = 0 } = usePendingBusinessesCount(canModerateCommunity);
  const administrationSectionCount =
    adminUnreadCount + pendingApprovalsCount + openAppIssueCount;

  const isMobileView = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.(`(max-width: ${MOBILE_BREAKPOINT})`).matches;

  useEffect(() => {
    if (isMobileView()) {
      setSidebarOpen(false);
    }
  }, []);

  const toggleSidebar = () => setSidebarOpen((open) => !open);
  const handleNavClick = () => {
    if (isMobileView()) {
      setSidebarOpen(false);
    }
  };
  const toggleSection = (section: SidebarSectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };
  const handleOverlayClick = () => setSidebarOpen(false);
  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: 'My Profile',
      onClick: () => {
        navigate('/profile');
        if (isMobileView()) {
          setSidebarOpen(false);
        }
      },
    },
    {
      key: 'logout',
      label: 'Logout',
      onClick: () => signOut(),
    },
  ];
  const profileName = profile?.full_name || user?.user_metadata?.full_name || user?.email || 'User';
  const profileAvatar = profile?.avatar || user?.user_metadata?.avatar_url;
  const profileEmail = user?.email || 'No email';
  const messagesActive = location.pathname.startsWith('/admin/users/messages');
  const notificationsActive = location.pathname.startsWith('/notifications');

  return (
    <Wrapper>
      {user && !sidebarOpen ? (
        <SidebarOpenButton type="button" onClick={toggleSidebar} aria-label="Open navigation menu">
          <FiMenu size={22} />
        </SidebarOpenButton>
      ) : null}
      {user && (
        <>
          <Sidebar $open={sidebarOpen}>
            <SidebarHeaderRow>
             
              <SidebarDashboardTitle>Dashboard</SidebarDashboardTitle>
              {sidebarOpen && (
                <MenuToggleButton
                  type="button"
                  onClick={toggleSidebar}
                  aria-label="Close navigation menu"
                >
                  <FiMenu size={22} />
                </MenuToggleButton>
              )}
            </SidebarHeaderRow>
            <SidebarNav>
              {isStaff ? (
              <>
                <SidebarSectionHeader
                  type="button"
                  onClick={() => toggleSection('dashboard')}
                  aria-expanded={openSections.dashboard}
                >
                  <SidebarSectionTitle>
                    <NavIcon>
                      <FaHome size={14} />
                    </NavIcon>
                    Dashboard
                  </SidebarSectionTitle>
                  <SidebarSectionControls
                    isOpen={openSections.dashboard}
                    count={unreadNotificationsCount}
                  />
                </SidebarSectionHeader>
                {openSections.dashboard && (
                  <>
                    {canViewDashboard && (
                      <SubSidebarLink to="/" end onClick={handleNavClick}>
                        <NavIcon>
                          <FaHome size={14} />
                        </NavIcon>
                        Home
                      </SubSidebarLink>
                    )}
                    {canViewDashboard && (
                      <SubSidebarLink to="/search" onClick={handleNavClick}>
                        <NavIcon>
                          <FaSearch size={14} />
                        </NavIcon>
                        Search
                      </SubSidebarLink>
                    )}
                    <SubSidebarLink to="/profile" onClick={handleNavClick}>
                      <NavIcon>
                        <FaUser size={14} />
                      </NavIcon>
                      My Profile
                    </SubSidebarLink>
                    <SubSidebarLink to="/notifications" onClick={handleNavClick}>
                      <NavIcon>
                        <FaBell size={14} />
                      </NavIcon>
                      Notifications
                      {unreadNotificationsCount > 0 ? (
                        <NavCountBadge>{unreadNotificationsCount}</NavCountBadge>
                      ) : null}
                    </SubSidebarLink>
                  </>
                )}

                {(canViewAnnouncements || canViewMarketing) && (
                  <>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('communications')}
                      aria-expanded={openSections.communications}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaBullhorn size={14} />
                        </NavIcon>
                        Communications
                      </SidebarSectionTitle>
                      <SidebarSectionControls isOpen={openSections.communications} />
                    </SidebarSectionHeader>
                    {openSections.communications && (
                      <>
                        {canViewAnnouncements && (
                          <SubSidebarLink to="/announcements" onClick={handleNavClick}>
                            <NavIcon>
                              <FaBullhorn size={14} />
                            </NavIcon>
                            Announcements
                          </SubSidebarLink>
                        )}
                        {canViewMarketing && (
                          <>
                            <SubSidebarLink to="/marketing" onClick={handleNavClick}>
                              <NavIcon>
                                <FaBullhorn size={14} />
                              </NavIcon>
                              Marketing
                            </SubSidebarLink>
                            <SubSidebarLink to="/marketing/social" onClick={handleNavClick}>
                              <NavIcon>
                                <FaBullhorn size={14} />
                              </NavIcon>
                              Social Media
                            </SubSidebarLink>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

                {canViewCommunitySection && (
                  <>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('community')}
                      aria-expanded={openSections.community}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaHandsHelping size={14} />
                        </NavIcon>
                        Community
                      </SidebarSectionTitle>
                      <SidebarSectionControls
                        isOpen={openSections.community}
                        count={pendingBusinessesCount}
                      />
                    </SidebarSectionHeader>
                    {openSections.community && (
                      <>
                        {canModerateCommunity && (
                          <SubSidebarLink to="/posts" onClick={handleNavClick}>
                            <NavIcon>
                              <FaRegCommentDots size={14} />
                            </NavIcon>
                            Posts
                          </SubSidebarLink>
                        )}
                        {canViewCommunityGroups && (
                          <SubSidebarLink to="/groups" onClick={handleNavClick}>
                            <NavIcon>
                              <FaUsers size={14} />
                            </NavIcon>
                            Groups
                          </SubSidebarLink>
                        )}
                        {canModerateCommunity && (
                          <SubSidebarLink to="/businesses" end onClick={handleNavClick}>
                            <NavIcon>
                              <FaStore size={14} />
                            </NavIcon>
                            Businesses
                          </SubSidebarLink>
                        )}
                        {canModerateCommunity && (
                          <SubSidebarLink to="/businesses/submitted" onClick={handleNavClick}>
                            <NavIcon>
                              <FaClock size={14} />
                            </NavIcon>
                            Submitted Businesses
                            {pendingBusinessesCount > 0 ? (
                              <NavCountBadge>{pendingBusinessesCount}</NavCountBadge>
                            ) : null}
                          </SubSidebarLink>
                        )}
                        {canViewGroupGoals && (
                          <SubSidebarLink to="/group-goals" onClick={handleNavClick}>
                            <NavIcon>
                              <FaChartLine size={14} />
                            </NavIcon>
                            Group Goals
                          </SubSidebarLink>
                        )}
                        {canModerateCommunity && (
                          <SubSidebarLink to="/prayers" onClick={handleNavClick}>
                            <NavIcon>
                              <FaPrayingHands size={14} />
                            </NavIcon>
                            Prayers
                          </SubSidebarLink>
                        )}
                      </>
                    )}
                  </>
                )}

                {canViewEventsFacilitiesSection && (
                  <>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('events')}
                      aria-expanded={openSections.events}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaCalendarAlt size={14} />
                        </NavIcon>
                        Events & Facilities
                      </SidebarSectionTitle>
                      <SidebarSectionControls isOpen={openSections.events} />
                    </SidebarSectionHeader>
                    {openSections.events && (
                      <>
                        {(canViewAllEventLinks || canManageAssignedEventRegistrants) && (
                          <SubSidebarLink to="/events" onClick={handleNavClick}>
                            <NavIcon>
                              <FaCalendarAlt size={14} />
                            </NavIcon>
                            All Events
                          </SubSidebarLink>
                        )}
                        {(canManageEvents || canSubmitEventRequests) && (
                          <SubSidebarLink to="/events/submission" onClick={handleNavClick}>
                            <NavIcon>
                              <FaPaperPlane size={14} />
                            </NavIcon>
                            Event Submission
                          </SubSidebarLink>
                        )}
                        {(canViewAllEventLinks || canManageAssignedEventRegistrants) && (
                          <SubSidebarLink to="/events/public-registrations" onClick={handleNavClick}>
                            <NavIcon>
                              <FaInbox size={14} />
                            </NavIcon>
                            {canViewAllEventLinks ? 'Public Registrations' : 'My Event Registrants'}
                          </SubSidebarLink>
                        )}
                        {canManageFacilities && (
                          <SubSidebarLink to="/facilities" onClick={handleNavClick}>
                            <NavIcon>
                              <FaCalendarAlt size={14} />
                            </NavIcon>
                            Facilities
                          </SubSidebarLink>
                        )}
                        {canManageVolunteers && (
                          <SubSidebarLink to="/volunteers/maintenance-reports" onClick={handleNavClick}>
                            <NavIcon>
                              <FaInbox size={14} />
                            </NavIcon>
                            Maintenance Reports
                          </SubSidebarLink>
                        )}
                      </>
                    )}
                  </>
                )}

                {canViewSermonsContentSection && (
                  <>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('sermons')}
                      aria-expanded={openSections.sermons}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaBible size={14} />
                        </NavIcon>
                        Sermons & Content
                      </SidebarSectionTitle>
                      <SidebarSectionControls isOpen={openSections.sermons} />
                    </SidebarSectionHeader>
                    {openSections.sermons && (
                      <>
                        <SubSidebarLink to="/sermon-series" end onClick={handleNavClick}>
                          <NavIcon>
                            <FaListUl size={14} />
                          </NavIcon>
                          Series
                        </SubSidebarLink>
                        <SubSidebarLink to="/sermons" onClick={handleNavClick}>
                          <NavIcon>
                            <FaMicrophoneAlt size={14} />
                          </NavIcon>
                          Sermons
                        </SubSidebarLink>
                        {(canManageSermonQuestions || canViewSermonQuestions) && (
                          <SubSidebarLink to="/questions" onClick={handleNavClick}>
                            <NavIcon>
                              <FaRegCommentDots size={14} />
                            </NavIcon>
                            Questions
                          </SubSidebarLink>
                        )}
                        {canManageContent && (
                          <SubSidebarLink to="/declarations" onClick={handleNavClick}>
                            <NavIcon>
                              <FaFileSignature size={14} />
                            </NavIcon>
                            Declarations
                          </SubSidebarLink>
                        )}
                        {canManageContent && (
                          <SubSidebarLink to="/devotionals" onClick={handleNavClick}>
                            <NavIcon>
                              <FaHeart size={14} />
                            </NavIcon>
                            Devotionals
                          </SubSidebarLink>
                        )}
                        {canManageContent && (
                          <SubSidebarLink to="/trivia" onClick={handleNavClick}>
                            <NavIcon>
                              <FaListUl size={14} />
                            </NavIcon>
                            Trivia
                          </SubSidebarLink>
                        )}
                        {canManageContent && (
                          <SubSidebarLink to="/trivia/results" onClick={handleNavClick}>
                            <NavIcon>
                              <FaListUl size={14} />
                            </NavIcon>
                            Trivia Results
                          </SubSidebarLink>
                        )}
                      </>
                    )}
                  </>
                )}

                {canViewServingVolunteersSection && (
                  <>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('serving')}
                      aria-expanded={openSections.serving}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaHandHoldingHeart size={14} />
                        </NavIcon>
                        Serving & Volunteers
                      </SidebarSectionTitle>
                      <SidebarSectionControls isOpen={openSections.serving} />
                    </SidebarSectionHeader>
                    {openSections.serving && (
                      <>
                        {canViewChurchPlanner && (
                          <SubSidebarLink to="/church-planner/dashboard" onClick={handleNavClick}>
                            <NavIcon>
                              <FaCalendarAlt size={14} />
                            </NavIcon>
                            Service Planner
                          </SubSidebarLink>
                        )}
                        {canManageVolunteers && (
                          <SubSidebarLink to="/volunteers" onClick={handleNavClick}>
                            <NavIcon>
                              <FaHandHoldingHeart size={14} />
                            </NavIcon>
                            Volunteer Opportunities
                          </SubSidebarLink>
                        )}
                        {canManageVolunteers && (
                          <SubSidebarLink to="/volunteers/requests" onClick={handleNavClick}>
                            <NavIcon>
                              <FaInbox size={14} />
                            </NavIcon>
                            Volunteer Requests
                          </SubSidebarLink>
                        )}
                        {canManageVolunteers && (
                          <SubSidebarLink to="/volunteers/categories" onClick={handleNavClick}>
                            <NavIcon>
                              <FaTags size={14} />
                            </NavIcon>
                            Volunteer Categories
                          </SubSidebarLink>
                        )}
                      </>
                    )}
                  </>
                )}

                {canViewNextStepsSection && (
                  <>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('nextSteps')}
                      aria-expanded={openSections.nextSteps}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaHandshake size={14} />
                        </NavIcon>
                        Next Steps & Engagement
                      </SidebarSectionTitle>
                      <SidebarSectionControls isOpen={openSections.nextSteps} />
                    </SidebarSectionHeader>
                    {openSections.nextSteps && (
                      <>
                        {(canManageVolunteers || canViewPartnerRequests) && (
                          <SubSidebarLink to="/partners/requests" onClick={handleNavClick}>
                            <NavIcon>
                              <FaHandshake size={14} />
                            </NavIcon>
                            New Partners
                          </SubSidebarLink>
                        )}
                        {canViewUsers && (
                          <SubSidebarLink to="/admin/users/welcome-in-opt-in" onClick={handleNavClick}>
                            <NavIcon>
                              <FaUserCheck size={14} />
                            </NavIcon>
                            Welcome In Opt-Ins
                          </SubSidebarLink>
                        )}
                        {canViewWelcomeInPartnerConversions && (
                          <SubSidebarLink
                            to="/admin/users/welcome-in-partners"
                            onClick={handleNavClick}
                          >
                            <NavIcon>
                              <FaHandshake size={14} />
                            </NavIcon>
                            Welcome In → Partners
                          </SubSidebarLink>
                        )}
                      </>
                    )}
                  </>
                )}

                {canManageKidsChurch && (
                  <>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('classes')}
                      aria-expanded={openSections.classes}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaChalkboardTeacher size={14} />
                        </NavIcon>
                        Classes & Kids
                      </SidebarSectionTitle>
                      <SidebarSectionControls
                        isOpen={openSections.classes}
                        count={kidsChurchUnreadCount}
                      />
                    </SidebarSectionHeader>
                    {openSections.classes && (
                      <>
                        <SubSidebarLink to="/classroom-teacher" onClick={handleNavClick}>
                          <NavIcon>
                            <FaChalkboardTeacher size={14} />
                          </NavIcon>
                          Classroom
                        </SubSidebarLink>
                        <SubSidebarLink to="/kids-church" onClick={handleNavClick}>
                          <NavIcon>
                            <FaChild size={14} />
                          </NavIcon>
                          Kids Church
                          {kidsChurchUnreadCount > 0 ? (
                            <NavCountBadge>{kidsChurchUnreadCount}</NavCountBadge>
                          ) : null}
                        </SubSidebarLink>
                      </>
                    )}
                  </>
                )}

                {canViewWebsiteSection && (
                  <>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('website')}
                      aria-expanded={openSections.website}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaTags size={14} />
                        </NavIcon>
                        Website
                      </SidebarSectionTitle>
                      <SidebarSectionControls
                        isOpen={openSections.website}
                        count={contactRequestsCount}
                      />
                    </SidebarSectionHeader>
                    {openSections.website && (
                      <>
                        <SubSidebarLink to="/admin/website" onClick={handleNavClick}>
                          <NavIcon>
                            <FaTags size={14} />
                          </NavIcon>
                          Website
                        </SubSidebarLink>
                        <SubSidebarLink to="/admin/website/store" onClick={handleNavClick}>
                          <NavIcon>
                            <FaTags size={14} />
                          </NavIcon>
                          Store
                        </SubSidebarLink>
                        {canViewUsers && canViewGrowth && (
                          <SubSidebarLink to="/growth" onClick={handleNavClick}>
                            <NavIcon>
                              <FaChartLine size={14} />
                            </NavIcon>
                            Local Engagement
                          </SubSidebarLink>
                        )}
                        {canViewUsers && (
                          <SubSidebarLink to="/admin/contact-requests" onClick={handleNavClick}>
                            <NavIcon>
                              <FaRegCommentDots size={14} />
                            </NavIcon>
                            Contact Requests
                            {contactRequestsCount > 0 ? (
                              <NavCountBadge>{contactRequestsCount}</NavCountBadge>
                            ) : null}
                          </SubSidebarLink>
                        )}
                        {canViewUsers && (
                          <SubSidebarLink to="/admin/assessments" onClick={handleNavClick}>
                            <NavIcon>
                              <FaListUl size={14} />
                            </NavIcon>
                            Assessments
                          </SubSidebarLink>
                        )}
                      </>
                    )}
                  </>
                )}

                {canViewAdministrationSection && (
                  <>
                    <SidebarDivider />
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => toggleSection('administration')}
                      aria-expanded={openSections.administration}
                    >
                      <SidebarSectionTitle>
                        <NavIcon>
                          <FaUserShield size={14} />
                        </NavIcon>
                        Administration
                      </SidebarSectionTitle>
                      <SidebarSectionControls
                        isOpen={openSections.administration}
                        count={administrationSectionCount}
                      />
                    </SidebarSectionHeader>
                    {openSections.administration && (
                      <>
                        {canViewUsers && (
                          <SubSidebarLink to="/admin/users" onClick={handleNavClick}>
                            <NavIcon>
                              <FaUserShield size={14} />
                            </NavIcon>
                            Users & Roles
                            {adminUnreadCount > 0 ? (
                              <NavCountBadge>{adminUnreadCount}</NavCountBadge>
                            ) : null}
                          </SubSidebarLink>
                        )}
                        {canViewUsers && (
                          <SubSidebarLink to="/admin/ncof-educators" onClick={handleNavClick}>
                            <NavIcon>
                              <FaChalkboardTeacher size={14} />
                            </NavIcon>
                            NCOF Educators
                          </SubSidebarLink>
                        )}
                        {canViewUsers && (
                          <SubSidebarLink to="/admin/students" onClick={handleNavClick}>
                            <NavIcon>
                              <FaChild size={14} />
                            </NavIcon>
                            Students
                          </SubSidebarLink>
                        )}
                        {canManageContent && (
                          <SubSidebarLink to="/admin/content-approval" onClick={handleNavClick}>
                            <NavIcon>
                              <FaRegCommentDots size={14} />
                            </NavIcon>
                            Content Approval
                            {pendingApprovalsCount > 0 ? (
                              <NavCountBadge>{pendingApprovalsCount}</NavCountBadge>
                            ) : null}
                          </SubSidebarLink>
                        )}
                        {canViewFinance && (
                          <SubSidebarLink to="/finance" onClick={handleNavClick}>
                            <NavIcon>
                              <FaReceipt size={14} />
                            </NavIcon>
                            Finance
                          </SubSidebarLink>
                        )}
                        {canViewSuperAdmin && (
                          <SubSidebarLink to="/super-admin" onClick={handleNavClick}>
                            <NavIcon>
                              <FaUserCog size={14} />
                            </NavIcon>
                            Super Admin
                          </SubSidebarLink>
                        )}
                        {canViewSuperAdmin && (
                          <SubSidebarLink to="/super-admin/app-issues" onClick={handleNavClick}>
                            <NavIcon>
                              <FaBug size={14} />
                            </NavIcon>
                            App Issues
                            {openAppIssueCount > 0 ? (
                              <NavCountBadge>{openAppIssueCount}</NavCountBadge>
                            ) : null}
                          </SubSidebarLink>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <SidebarLink to="/profile" onClick={handleNavClick}>
                  <NavIcon>
                    <FaUser size={16} />
                  </NavIcon>
                  My Profile
                </SidebarLink>
                <SidebarLink to="/notifications" onClick={handleNavClick}>
                  <NavIcon>
                    <FaBell size={16} />
                  </NavIcon>
                  Notifications
                  {unreadNotificationsCount > 0 ? (
                    <NavCountBadge>{unreadNotificationsCount}</NavCountBadge>
                  ) : null}
                </SidebarLink>
                {(canManageEvents || canSubmitEventRequests) && (
                  <SidebarLink to="/events/submission" onClick={handleNavClick}>
                    <NavIcon>
                      <FaPaperPlane size={16} />
                    </NavIcon>
                    Event Submission
                  </SidebarLink>
                )}
              </>
              )}
            </SidebarNav>
            <SidebarFooter>
              <Dropdown menu={{ items: profileMenuItems }} trigger={['click']} placement="topLeft">
                <SidebarProfileTrigger type="button" aria-label="Open profile options">
                  <ReactAvatar name={profileName} src={profileAvatar} size="36" round />
                  <SidebarProfileMeta>
                    <SidebarProfileName>{profileName}</SidebarProfileName>
                    <SidebarProfileEmail>{profileEmail}</SidebarProfileEmail>
                  </SidebarProfileMeta>
                </SidebarProfileTrigger>
              </Dropdown>
            </SidebarFooter>
          </Sidebar>
          <SidebarOverlay
            role="presentation"
            aria-hidden={!sidebarOpen}
            $visible={sidebarOpen}
            onClick={handleOverlayClick}
          />
        </>
      )}
      <Content $sidebarOpen={Boolean(user && sidebarOpen)}>
        <ContentShell>
          {user ? (
            <ContentTopBar>
              <TopBarActions>
                {canViewUsers ? (
                  <TopBarIconButton
                    type="button"
                    onClick={() => navigate('/admin/users/messages')}
                    aria-label={
                      adminUnreadCount > 0
                        ? `Open messages (${adminUnreadCount} unread)`
                        : 'Open messages'
                    }
                    title="Messages"
                    $active={messagesActive}
                  >
                    <FaRegCommentDots size={18} />
                    {adminUnreadCount > 0 ? (
                      <TopBarIconBadge>
                        {adminUnreadCount > 99 ? '99+' : adminUnreadCount}
                      </TopBarIconBadge>
                    ) : null}
                  </TopBarIconButton>
                ) : null}
                <TopBarIconButton
                  type="button"
                  onClick={() => navigate('/notifications')}
                  aria-label={
                    unreadNotificationsCount > 0
                      ? `Open notifications (${unreadNotificationsCount} unread)`
                      : 'Open notifications'
                  }
                  title="Notifications"
                  $active={notificationsActive}
                >
                  <FaBell size={18} />
                  {unreadNotificationsCount > 0 ? (
                    <TopBarIconBadge>
                      {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                    </TopBarIconBadge>
                  ) : null}
                </TopBarIconButton>
              </TopBarActions>
            </ContentTopBar>
          ) : null}
          <ContentBody>{children}</ContentBody>
        </ContentShell>
      </Content>
      {user ? <KimiChatWidget /> : null}
    </Wrapper>
  );
};

export default Layout;
